import { Container, Graphics } from "pixi.js";
import type { SustainSpan } from "@/lib/track/lane";

export type PedalRibbonProps = {
  /** Le pédalage que le morceau écrit ; vide quand il n'en écrit pas. */
  written: readonly SustainSpan[];
  /** Celui qui se joue : le tien, ou celui de l'interprète qu'on regarde. */
  played: readonly SustainSpan[];
  fromTick: number;
  toTick: number;
  height: number;
};

/**
 * Le ruban de pédale : le `Ped. ___` de la partition, qui défile avec elle.
 *
 * # Deux rubans, et c'est le sujet
 *
 * Celui du morceau, quand le fichier écrit son pédalage, et celui qui se joue.
 * Les comparer est toute la leçon : où tu tiens à travers un changement
 * d'harmonie, où tu relèves trop tard, où tu n'as pas pédalé du tout. Un
 * morceau sur trois seulement écrit le sien ; les autres n'affichent que le
 * jeu, et la bande du morceau reste vide plutôt que de se déplacer — une
 * position qui voudrait dire deux choses selon le fichier ne se lirait plus.
 *
 * Celui qui se joue n'est pas toujours le tien : en récital, c'est le pied de
 * l'interprète qu'on regarde, comme ce sont ses notes qui descendent. Le tien
 * reprend la bande quand il n'y a personne à regarder.
 *
 * # Ce que les deux ne disent pas pareil
 *
 * Celui du morceau est écrit, donc il se dessine **devant** la tête de
 * lecture : la pédale se voit venir, comme les notes. Celui qui se joue
 * s'arrête au présent, parce que le pied est encore en bas et que personne ne
 * sait quand il remontera (voir `Lane.getSustainSpans`). Ce présent est la
 * tête de lecture pour ton pied, et celui de la séance pour un interprète, qui
 * joue un délai de restitution devant elle — voir `laneNow`.
 *
 * # Achromatique, et au bord
 *
 * La pédale n'est pas une hauteur : elle n'a rien à faire dans les couleurs
 * des mains ni dans celles des degrés. Elle prend le gris des marges, et la
 * lisière gauche du roll, sous les graduations de la règle — qui passent
 * par-dessus, et dont l'ouverture au survol recouvre tout : lire une mesure et
 * lire le pédalage ne sont pas le même geste, et ils ne se disputent donc
 * jamais la même seconde.
 *
 * Chaque enfoncement porte un petit cap plus clair : c'est le mouvement qui
 * compte, la tenue n'est que sa conséquence.
 */
export class PedalRibbon {
  private readonly graphics = new Graphics();

  constructor(stage: Container) {
    stage.addChild(this.graphics);
  }

  destroy(): void {
    this.graphics.destroy();
  }

  draw(props: PedalRibbonProps): void {
    const { written, played, fromTick, toTick, height } = props;

    this.graphics.clear();

    if (written.length === 0 && played.length === 0) {
      return;
    }

    const pxPerTick = height / (toTick - fromTick);

    this.band(written, WRITTEN_X, WRITTEN_ALPHA, fromTick, height, pxPerTick);
    this.band(played, PLAYED_X, PLAYED_ALPHA, fromTick, height, pxPerTick);
  }

  private band(
    spans: readonly SustainSpan[],
    x: number,
    alpha: number,
    fromTick: number,
    height: number,
    pxPerTick: number,
  ): void {
    for (const span of spans) {
      const yTop = height - (span.to - fromTick) * pxPerTick;
      const yBottom = height - (span.from - fromTick) * pxPerTick;

      this.graphics
        .rect(x, yTop, BAND_WIDTH, Math.max(MIN_HEIGHT_PX, yBottom - yTop))
        .fill({ color: PEDAL_COLOR, alpha });

      // L'enfoncement, quand il tombe dans la fenêtre : un cap plus clair et
      // un peu plus large, qui se voit sans qu'on le cherche.
      if (yBottom <= height) {
        this.graphics
          .rect(x - CAP_OVERHANG, yBottom - CAP_HEIGHT, BAND_WIDTH + CAP_OVERHANG * 2, CAP_HEIGHT)
          .fill({ color: PEDAL_COLOR, alpha: Math.min(1, alpha + CAP_ALPHA_GAIN) });
      }
    }
  }
}

/** Le gris des marges du roll, celui des tonalités. */
const PEDAL_COLOR = 0xcbd5e1;

/**
 * Les deux bandes, en partant du bord.
 *
 * Neuf pixels en tout, et c'est tout ce qu'il y a : les graduations de la
 * règle commencent au septième et passent par-dessus la seconde bande — une
 * mesure barre son ruban comme une graduation barre une jauge —, et les noms
 * d'accords commencent au huitième. Plus large, la pédale mangerait l'un ou
 * l'autre ; plus étroite, elle ne se verrait plus sur un fond presque noir.
 */
const WRITTEN_X = 0;
const PLAYED_X = 5;
const BAND_WIDTH = 4;

/** La partition est une consigne, le jeu est un fait : il est le plus visible. */
const WRITTEN_ALPHA = 0.45;
const PLAYED_ALPHA = 0.85;

const CAP_HEIGHT = 3;
const CAP_OVERHANG = 1;
const CAP_ALPHA_GAIN = 0.3;

/** Une pédale plus brève que ça reste visible, sinon elle disparaîtrait. */
const MIN_HEIGHT_PX = 2;
